// src/pages/shippment/CreateShippingTabs.jsx
import React, { useEffect, useState, useCallback } from "react";
import "../../utils/patchResizeObserver";
import ShipmentDetailsTab from "./tabs/ShipmentDetailsTab";
import AddressesTab from "./tabs/AddressesTab";
import CarrierTab from "./tabs/CarrierTab";
import LoadTab from "./tabs/LoadTab";
import UdfTab from "./tabs/UdfTab";
import BillToTab from "./tabs/BillToTab.jsx";
import InvoiceTab from "./tabs/InvoiceTab.jsx";
import Loader from "../../components/Loader";

import { createShipment, updateShipment } from "../../services/shipment/shipment.jsx"; // create + update

import SKUSearchResults from "./SKUSearchResults.jsx";
import StopsSection from "./StopsSection.jsx";

import { useSelector, useDispatch } from "react-redux";
import { setLoadTable } from "../../redux/action/skuTable.action";

import {
    buildDefaultShipmentFormData,
    shipmentHeaderFields,
    sourceSellerAddressFields,
    destinationBuyerAddressFields,
    carrierFieldsPP,
    loadFieldsPP,
    udfFields,
    billToFields,     // ⬅️ added
    invoiceFields,    // ⬅️ added
} from "./formFields/shippmentTabsFields.jsx";

import { toast } from "../../components/AppToast";
import ConfirmDialog from "../../components/ConfirmDialog";
import { sanitizeFormData } from "../../utils/sanitizeFormData.js";

export default function CreateShippingTabs({
    initialValues = {},
    onCancel,
    onSubmit = () => { },
    isEdit = false,
    viewOnly = false,
    showConfirmButton,
}) {
    const [activeTab, setActiveTab] = useState(0);

    // keep initial state as before
    const [formData, setFormData] = useState(() => ({
        ...buildDefaultShipmentFormData(),
        ...initialValues,
    }));

    // IMPORTANT: sync formData whenever initialValues prop changes (opening a row should populate the form)
    const initialKeyRef = React.useRef(null);

    useEffect(() => {
        try {
            const iv = initialValues || {};

            // console.log("syncing formData from initialValues prop change", iv);

            // Stable key for this record (prevents re-initting same row)
            const currentKey = iv._id ?? iv.id ?? iv.shipmentNumber ?? "__new";
            if (initialKeyRef.current !== null && initialKeyRef.current === currentKey) {
                return;
            }
            initialKeyRef.current = currentKey;

            const pickFirst = (...vals) => vals.find((v) => v !== undefined && v !== null);

            // ---- FLATTEN: shipmentDetails -> header flat fields ----
            const sd = iv.shipmentDetails || {};
            const flattenShipmentDetails = {
                shipmentNumber: pickFirst(sd.shipmentNumber, iv.shipmentNumber, null),
                externalShipmentNumber: pickFirst(sd.externalShipmentNumber, iv.externalShipmentNumber, sd.externalId, iv.externalId, null),
                loadId: pickFirst(sd.loadId, iv.loadId, null),
                source: pickFirst(sd.source, iv.source, null),
                createUsing: pickFirst(sd.createUsing, iv.createUsing, null),
                status: pickFirst(sd.status, iv.status, "New"),
                shipmentDate: pickFirst(sd.shipmentDate, iv.shipmentDate, null),
                closedDate: pickFirst(sd.closeDate, sd.closedDate, iv.closedDate, null),
                totalQty: pickFirst(sd.totalOrderedQuantity, sd.totalQty, iv.totalQty, null),
                totalWeight: pickFirst(sd.totalWeight, iv.totalWeight, null),
                totalVolume: pickFirst(sd.totalVolume, iv.totalVolume, null),
            };

            // ---- FLATTEN: sourceDetail -> source* fields ----
            const src = iv.sourceDetail || {};
            const flattenSource = {
                sourceName: pickFirst(src.sourceName, iv.sourceName, null),
                sourceAddressLine1: pickFirst(src.addressLine1, iv.sourceAddressLine1, null),
                sourceAddressLine2: pickFirst(src.addressLine2, iv.sourceAddressLine2, null),
                sourceAddressLine3: pickFirst(src.addressLine3, iv.sourceAddressLine3, null),
                sourceAddressLine4: pickFirst(src.addressLine4, iv.sourceAddressLine4, null),
                sourceCity: pickFirst(src.city, iv.sourceCity, null),
                sourceState: pickFirst(src.state, iv.sourceState, null),
                sourceCountry: pickFirst(src.country, iv.sourceCountry, null),
                sourceContact: pickFirst(src.contact, iv.sourceContact, null),
                sourceEmail: pickFirst(src.email, iv.sourceEmail, null),
            };

            // ---- FLATTEN: destination -> destination* fields ----
            const dst = iv.destination || {};
            const flattenDestination = {
                destinationName: pickFirst(dst.name, iv.destinationName, null),
                destinationAddressLine1: pickFirst(dst.addressLine1, iv.destinationAddressLine1, null),
                destinationAddressLine2: pickFirst(dst.addressLine2, iv.destinationAddressLine2, null),
                destinationAddressLine3: pickFirst(dst.addressLine3, iv.destinationAddressLine3, null),
                destinationAddressLine4: pickFirst(dst.addressLine4, iv.destinationAddressLine4, null),
                destinationCity: pickFirst(dst.city, iv.destinationCity, null),
                destinationState: pickFirst(dst.state, iv.destinationState, null),
                destinationCountry: pickFirst(dst.country, iv.destinationCountry, null),
                destinationContact: pickFirst(dst.contact, iv.destinationContact, null),
                destinationEmail: pickFirst(dst.email, iv.destinationEmail, null),
            };

            // ---- FLATTEN: billTo -> billTo* fields ----
            const bt = iv.billTo || {};
            const flattenBillTo = {
                billToName: pickFirst(bt.name, iv.billToName, null),
                billToAddressLine1: pickFirst(bt.addressLine1, iv.billToAddressLine1, null),
                billToAddressLine2: pickFirst(bt.addressLine2, iv.billToAddressLine2, null),
                billToAddressLine3: pickFirst(bt.addressLine3, iv.billToAddressLine3, null),
                billToAddressLine4: pickFirst(bt.addressLine4, iv.billToAddressLine4, null),
                billToCity: pickFirst(bt.city, iv.billToCity, null),
                billToState: pickFirst(bt.state, iv.billToState, null),
                billToCountry: pickFirst(bt.country, iv.billToCountry, null),
                billToContact: pickFirst(bt.contact, iv.billToContact, null),
                billToEmail: pickFirst(bt.email, iv.billToEmail, null),
                billToSameAsDestination: Boolean(
                    pickFirst(bt.billToSameAsDestination, iv.billToSameAsDestination, false)
                ),
            };

            // ---- FLATTEN: invoice -> invoice* fields ----
            const inv = iv.invoice || {};
            const flattenInvoice = {
                invoiceNumber: pickFirst(inv.invoiceNumber, iv.invoiceNumber, null),
                invoiceDate: pickFirst(inv.invoiceDate, iv.invoiceDate, null),
                invoiceType: pickFirst(inv.invoiceType, iv.invoiceType, null),
                invoiceAmount: pickFirst(inv.invoiceAmount, iv.invoiceAmount, null),
                paymentTerms: pickFirst(inv.paymentTerms, iv.paymentTerms, null),
            };

            // ---- FLATTEN: carrier -> carrier* fields ----
            const car = iv.carrier || {};
            const flattenCarrier = {
                shippingMode: pickFirst(car.shippingMode, iv.shippingMode, null),
                carrierName: pickFirst(car.carrierName, iv.carrierName, null),
                awbNumber: pickFirst(car.awbNumber, iv.awbNumber, null),
                carrierAddressLine1: pickFirst(car.addressLine1, iv.carrierAddressLine1, null),
                carrierAddressLine2: pickFirst(car.addressLine2, iv.carrierAddressLine2, null),
                carrierCity: pickFirst(car.city, iv.carrierCity, null),
                carrierState: pickFirst(car.state, iv.carrierState, null),
                carrierCountry: pickFirst(car.country, iv.carrierCountry, null),
                carrierContact: pickFirst(car.contact, iv.carrierContact, null),
                carrierEmail: pickFirst(car.email, iv.carrierEmail, null),
            };

            // ---- FLATTEN: load -> load/date fields ----
            const ld = iv.load || {};
            const flattenLoad = {
                vehicleNumber: pickFirst(ld.vehicleNumber, iv.vehicleNumber, null),
                vehicleDetails: pickFirst(ld.vehicleDetails, iv.vehicleDetails, null),
                driverDetails: pickFirst(ld.driverDetails, iv.driverDetails, null),
                expectedDepartureDate: pickFirst(ld.expectedDepartureDate, iv.expectedDepartureDate, null),
                actualDepartureDate: pickFirst(ld.actualDepartureDate, iv.actualDepartureDate, null),
                expectedArrivalDate: pickFirst(ld.expectedArrivalDate, iv.expectedArrivalDate, null),
                actualArrivalDate: pickFirst(ld.actualArrivalDate, iv.actualArrivalDate, null),
            };

            // Final merged state
            const merged = {
                ...buildDefaultShipmentFormData(),
                ...iv, // keep original object for anything else referenced
                ...flattenShipmentDetails,
                ...flattenSource,
                ...flattenDestination,
                ...flattenBillTo,
                ...flattenInvoice,
                ...flattenCarrier,
                ...flattenLoad,
            };

            setFormData(merged);
            // --- Seed SHO defaults for dates on CREATE only ---
            if (!isEdit && !viewOnly) {
                setFormData(prev => {
                    const createdBaseISO =
                        prev?.createdAt ||
                        prev?.shipmentDetails?.createdAt ||
                        new Date().toISOString();

                    const toISO = (d) => new Date(d).toISOString();
                    const addDaysISO = (iso, days) => {
                        const dt = new Date(iso);
                        dt.setDate(dt.getDate() + days);
                        return dt.toISOString();
                    };

                    return {
                        ...prev,
                        actualDepartureDate:
                            prev.actualDepartureDate || toISO(createdBaseISO),
                        expectedArrivalDate:
                            prev.expectedArrivalDate || addDaysISO(createdBaseISO, 1),
                    };
                });
            }

        } catch (err) {
            console.error("Failed to sync formData from initialValues", err);
            setFormData((prev) => ({ ...prev, ...(initialValues || {}) }));
        }
    }, [initialValues]);



    // useEffect(() => {
    //     console.log("BillTo formData snapshot:", {
    //         billToName: formData.billToName,
    //         billTo: formData.billTo,        // kabhi kabhi nested aa jata hai
    //     });
    // }, [formData.billToName]);


    const [selectedRowsData, setSelectedRowsData] = useState({});
    const loadTable = useSelector((state) => state.skutable?.TableDetails || {});
    const dispatch = useDispatch();
    const [open, setOpen] = useState(false);
    const [groupToRemove, setGroupToRemove] = useState(null);
    const [loading, setLoading] = useState(false);
    // errors state
    const [errors, setErrors] = useState({});

    const [confirmDeliveryOpen, setConfirmDeliveryOpen] = useState(false);
    const [confirmDeliveryGroupKey, setConfirmDeliveryGroupKey] = useState(null);

    const tabs = [
        { label: "Header", component: ShipmentDetailsTab },
        { label: "Addresses", component: AddressesTab },
        { label: "Bill To", component: BillToTab },
        { label: "Invoice", component: InvoiceTab },
        { label: "Carrier", component: CarrierTab },
        { label: "Load / Dates", component: LoadTab },
        // { label: "POD", component: PodTab },
        { label: "UDF   ", component: UdfTab },
    ];

    useEffect(() => {
        if (!isEdit && !viewOnly) dispatch(setLoadTable({}));
    }, [dispatch, isEdit, viewOnly]);

    // Helper: map field -> tab index. Use your fields arrays to detect.
    function fieldToTabIndex(fieldName) {
        const groups = [
            { idx: 0, fields: shipmentHeaderFields || [] },
            {
                idx: 1,
                fields: [...(sourceSellerAddressFields || []), ...(destinationBuyerAddressFields || [])],
            },
            { idx: 2, fields: billToFields || [] },     // mapped Bill To
            { idx: 3, fields: invoiceFields || [] },    // mapped Invoice
            { idx: 4, fields: carrierFieldsPP || [] },
            { idx: 5, fields: loadFieldsPP || [] },
            { idx: 6, fields: [] }, // PodTab - add fields if available
            { idx: 7, fields: udfFields || [] },
        ];

        for (const g of groups) {
            if (!g.fields) continue;
            if (g.fields.some((f) => f.name === fieldName)) return g.idx;
        }
        // fallback to header tab
        return 0;
    }

    function validateRequiredFields(currFormData = formData) {
        const allFields = [
            ...(shipmentHeaderFields || []),
            ...(sourceSellerAddressFields || []),
            ...(destinationBuyerAddressFields || []),
            ...(billToFields || []),     // include Bill To
            ...(invoiceFields || []),    // include Invoice
            ...(carrierFieldsPP || []),
            ...(isEdit ? (loadFieldsPP || []) : []),
            ...(udfFields || []),
        ];

        const missing = [];

        for (const f of allFields) {
            if (!f || !f.required) continue;

            // respect conditional (if field not visible, skip)
            if (f.conditional) {
                const condField = f.conditional.field;
                const condValue = f.conditional.value;
                if ((currFormData?.[condField] ?? null) !== condValue) continue;
            }

            const val = currFormData[f.name];

            let isMissing = false;
            if (val === null || val === undefined) isMissing = true;
            else if (typeof val === "string" && val.trim() === "") isMissing = true;
            else if (Array.isArray(val) && val.length === 0) isMissing = true;

            if (isMissing) {
                missing.push({ name: f.name, label: f.label || f.name });
            }
        }

        const valid = missing.length === 0;
        const firstMissing = missing.length > 0 ? missing[0].name : null;
        return { valid, missing, firstMissingName: firstMissing };
    }

    // 🔴 does a tab have any errors (for red badge + style)?
    const tabHasErrors = (tabIdx) => {
        const byIdx = {
            0: shipmentHeaderFields || [],
            1: [...(sourceSellerAddressFields || []), ...(destinationBuyerAddressFields || [])],
            2: billToFields || [],
            3: invoiceFields || [],
            4: carrierFieldsPP || [],
            5: loadFieldsPP || [],
            6: [], // POD (if you add fields later)
            7: udfFields || [],
        };
        const fields = byIdx[tabIdx] || [];
        return fields.some((f) => !!errors?.[f.name]);
    };

    const handleConfirmDeliveryFromHeader = useCallback((groupKey) => {
        // store the groupKey so modal can show items / pod etc
        setConfirmDeliveryGroupKey(groupKey);
        setConfirmDeliveryOpen(true);
    }, []);

    useEffect(() => {
        try {
            if (initialValues && initialValues.__openConfirmDelivery) {
                // merge incoming initialValues into formData so POD details show
                setFormData((prev) => {
                    const merged = { ...prev, ...initialValues };
                    // remove the marker so it doesn't persist on further edits
                    if (merged.__openConfirmDelivery) delete merged.__openConfirmDelivery;
                    return merged;
                });

                const groupKey =
                    initialValues.shipmentNumber ?? initialValues._id ?? initialValues.id ?? null;
                setConfirmDeliveryGroupKey(groupKey);
                setConfirmDeliveryOpen(true);
            }
        } catch (err) {
            console.error("Auto open confirm delivery effect error", err);
        }
    }, [initialValues]);

    const handleChange = (e) => {
        if (viewOnly) return;
        const { name, value, type, checked, files } = e.target ?? {};
        if (!name) return;

        const toSet = type === "checkbox" ? checked : type === "file" ? files : value;

        // live-clear error for this field
        setErrors((prev) => {
            if (!prev || !prev[name]) return prev;
            const copy = { ...prev };
            delete copy[name];
            return copy;
        });

        setFormData((prev) => ({ ...prev, [name]: toSet }));
    };

    const handleSelectionChange = useCallback((data) => {
        setSelectedRowsData(data);
    }, []);

    const handleDispatchQtyChange = (groupKey, index, value) => {
        const updated = { ...loadTable };
        updated[groupKey] = [...(updated[groupKey] || [])];
        updated[groupKey][index] = { ...updated[groupKey][index], dispatchQty: value };
        dispatch(setLoadTable(updated));
    };

    const handleRemoveRow = (groupKey, index) => {
        const updated = { ...loadTable };
        if (Array.isArray(updated[groupKey]) && index >= 0 && index < updated[groupKey].length) {
            updated[groupKey] = updated[groupKey].filter((_, i) => i !== index);
            // If the group is now empty, remove it
            if (updated[groupKey].length === 0) {
                delete updated[groupKey];
            }
            dispatch(setLoadTable(updated));
            toast.success("Row removed successfully", { position: "top-center", autoClose: 1500 });
        }
    };

    // small numeric helper
    const _toNum = (v) => {
        if (v === null || v === undefined || v === "") return 0;
        const n = Number(v);
        return Number.isFinite(n) ? n : 0;
    };

    const computeShipmentStatusFromTable = (table = {}) => {
        try {
            // flatten all rows from groups (ignore meta keys starting with __)
            const allRows = Object.entries(table || {})
                .filter(([k]) => typeof k === "string" && !k.startsWith("__"))
                .flatMap(([k, arr]) => (Array.isArray(arr) ? arr : []));

            if (allRows.length === 0) return "New";

            const deliveredCount = allRows.filter(
                (r) =>
                    r.deliveryState === "delivered" ||
                    (r._derived &&
                        _toNum(r._derived.deliveredQty) >=
                        _toNum(r._derived.expectedQty ?? r.expectedQty ?? r.orderedQty))
            ).length;

            if (deliveredCount === allRows.length) return "Delivered";
            if (deliveredCount > 0 && deliveredCount < allRows.length) return "Partially Delivered";

            const loadExists = Boolean(formData?.loadId || formData?.vehicleNumber || formData?.load);
            if (loadExists) return "In Transit";

            return "New";
        } catch (err) {
            console.warn("computeShipmentStatusFromTable error", err);
            return "New";
        }
    };

    // ---------- NEW: performConfirmDelivery (updated) ----------
    const performConfirmDelivery = (groupKey, payload = {}) => {
        try {
            // accept fallback keys inside payload
            const resolvedGroupKey =
                groupKey ??
                payload.groupKey ??
                payload.group ??
                payload.purchaseOrder ??
                payload.skuGroup ??
                null;

            console.debug("performConfirmDelivery called", { groupKey, resolvedGroupKey, payload });

            if (!resolvedGroupKey) {
                toast.error("No group selected to confirm.");
                console.error("performConfirmDelivery: cannot resolve groupKey from args", {
                    groupKey,
                    payload,
                });
                return;
            }

            const gk = resolvedGroupKey;

            // copy current redux loadTable
            const updated = { ...(loadTable || {}) };
            const groupRows = Array.isArray(updated[gk]) ? [...updated[gk]] : [];

            if (!groupRows.length) {
                toast.error("No items found to confirm for this group.");
                return;
            }

            // determine indexes to confirm
            let indexesToConfirm = [];
            if (Array.isArray(payload.lineIndexes) && payload.lineIndexes.length > 0) {
                indexesToConfirm = payload.lineIndexes
                    .map((i) => Number(i))
                    .filter((i) => Number.isFinite(i) && i >= 0 && i < groupRows.length);
            } else if (Array.isArray(payload.lineItemIds) && payload.lineItemIds.length > 0) {
                payload.lineItemIds.forEach((id) => {
                    const idx = groupRows.findIndex(
                        (r) => String(r.lineItemId ?? r.lineNo ?? r.__originalIndex__) === String(id)
                    );
                    if (idx >= 0 && !indexesToConfirm.includes(idx)) indexesToConfirm.push(idx);
                });
            } else {
                // confirm all rows
                indexesToConfirm = groupRows.map((_, i) => i);
            }

            if (indexesToConfirm.length === 0) {
                toast.info("No valid lines selected to confirm.");
                return;
            }

            // apply confirms
            indexesToConfirm.forEach((i) => {
                const r = groupRows[i];
                if (!r) return;
                const expected =
                    Number(r._derived?.expectedQty ?? r.expectedQty ?? r.orderedQty ?? 0) || 0;
                const derived = { ...(r._derived || {}) };
                derived.deliveredQty = expected;
                groupRows[i] = {
                    ...r,
                    deliveryState: "delivered",
                    _derived: derived,
                    __confirmedLine: true,
                    podDetails: payload.podDetails ?? r.podDetails ?? null,
                    podUpload: payload.podUpload ?? r.podUpload ?? null,
                };
            });

            // write back
            updated[gk] = groupRows;
            updated[gk].__allConfirmed = updated[gk].every(
                (rr) =>
                    rr.deliveryState === "delivered" ||
                    (rr._derived &&
                        Number(rr._derived.deliveredQty || 0) >=
                        Number(rr._derived.expectedQty || rr.expectedQty || rr.orderedQty || 0))
            );

            updated.__lastAddedTable = updated.__lastAddedTable || gk;
            dispatch(setLoadTable(updated));

            // recompute overall status
            const newStatusRaw = computeShipmentStatusFromTable(updated);
            // Map to user-friendly labels:
            let mappedStatus = newStatusRaw;
            if (newStatusRaw === "Delivered") mappedStatus = "Complete";
            else if (newStatusRaw === "Partially Delivered") mappedStatus = "Partially Completed";
            else if (newStatusRaw === "In Transit") mappedStatus = "In Transit";
            else if (newStatusRaw === "New") mappedStatus = "New";
            else mappedStatus = newStatusRaw;

            setFormData((prev) => {
                const next = { ...(prev || {}) };
                next.status = mappedStatus;
                next.shipmentDetails = { ...(next.shipmentDetails || {}), status: mappedStatus };
                next.header = { ...(next.header || {}), ...(next.shipmentDetails || {}) };
                return next;
            });

            // clear any per-group selections in local state (if you keep selectedRowsData)
            setSelectedRowsData((prev) => {
                const copy = { ...(prev || {}) };
                if (copy[gk]) delete copy[gk];
                return copy;
            });

            toast.success(
                indexesToConfirm.length === 1
                    ? "Line delivery confirmed"
                    : `Confirmed ${indexesToConfirm.length} lines`,
                { position: "top-center", autoClose: 1800 }
            );
        } catch (err) {
            console.error("performConfirmDelivery error", err);
            toast.error("Failed to confirm delivery.");
        }
    };
    // ---------- end performConfirmDelivery ---------->>
    const handleSubmit = async (e) => {
        e?.preventDefault?.();
        if (viewOnly) {
            onCancel?.();
            return;
        }

        // 1) Required fields validation (as-is)
        const { valid, missing, firstMissingName } = validateRequiredFields();
        if (!valid) {
            const errObj = {};
            missing.forEach((m) => {
                errObj[m.name] = `${m.label} is required`;
            });
            setErrors(errObj);

            const labels = missing.slice(0, 6).map((m) => m.label);
            toast.error(`Please fill required fields: ${labels.join(", ")}`, {
                position: "top-center",
                autoClose: 4000,
            });

            const focusFieldByName = (fieldName) => {
                const tabIndex = fieldToTabIndex(fieldName);
                setActiveTab(tabIndex);
                setTimeout(() => {
                    const elById = document.getElementById(fieldName);
                    const elByName = document.getElementsByName(fieldName)[0];
                    const el = elById || elByName;
                    if (el && typeof el.focus === "function") {
                        el.focus();
                        el.scrollIntoView?.({ behavior: "smooth", block: "center" });
                    }
                }, 160);
            };

            focusFieldByName(firstMissingName);
            return;
        }

        // clear previous errors
        setErrors({});

        // 2) SKU table: at least one selected row OR items in stops (CREATE & UPDATE both)
        const selectedEntries = Object.entries(selectedRowsData || {})
            .filter(([_, arr]) => Array.isArray(arr) && arr.length > 0);

        // Check if there are any items in stops (from loadTable)
        const stops = Array.isArray(formData?.stops) && formData.stops.length > 0
            ? formData.stops
            : (() => {
                const stopIds = new Set();
                Object.keys(selectedRowsData || {}).forEach(key => {
                    const match = String(key).match(/^STOP_(\d+)$/);
                    if (match) stopIds.add(Number(match[1]));
                });
                Object.keys(loadTable || {}).forEach(key => {
                    const match = String(key).match(/^STOP_(\d+)$/);
                    if (match) stopIds.add(Number(match[1]));
                });
                return Array.from(stopIds).sort((a, b) => a - b).map(id => ({ id, orderNumber: "" }));
            })();

        const hasItemsInStops = stops.some(stop => {
            const stopGroupKey = `STOP_${stop.id}`;
            const stopRows = Array.isArray(loadTable?.[stopGroupKey]) ? loadTable[stopGroupKey] : [];
            return stopRows.length > 0;
        });

        if (selectedEntries.length === 0 && !hasItemsInStops) {
            setErrors(prev => ({
                ...(prev || {}),
                lineItems: "Please select at least one line item from the PO/SKU table or add items to stops",
            }));
            toast.error("Select at least one line item before submitting the shipment.", {
                position: "top-center",
                autoClose: 4000,
            });
            setTimeout(() => {
                const el =
                    document.querySelector(".sku-search-results") ||
                    document.getElementById("skuTable") ||
                    document.querySelector(".search-results") ||
                    document.querySelector(".table-navbar");
                el?.scrollIntoView({ behavior: "smooth", block: "center" });
            }, 120);
            return;
        }

        // 3) Validate dispatchQty > 0 for all selected rows AND items in stops
        const invalidDispatch = [];
        selectedEntries.forEach(([groupKey, rows]) => {
            rows.forEach((r, i) => {
                const dq = Number(r.dispatchQty ?? r._derived?.dispatchQty ?? 0);
                if (!Number.isFinite(dq) || dq <= 0) {
                    invalidDispatch.push({ groupKey, index: i });
                }
            });
        });

        // Also validate dispatchQty for items in stops
        stops.forEach((stop) => {
            const stopGroupKey = `STOP_${stop.id}`;
            const stopRows = Array.isArray(loadTable?.[stopGroupKey]) ? loadTable[stopGroupKey] : [];
            stopRows.forEach((r, i) => {
                const dq = Number(r.dispatchQty ?? r._derived?.dispatchQty ?? 0);
                if (!Number.isFinite(dq) || dq <= 0) {
                    invalidDispatch.push({ groupKey: stopGroupKey, index: i });
                }
            });
        });

        if (invalidDispatch.length > 0) {
            toast.error("Enter Dispatch Qty (> 0) for all rows.", {
                position: "top-center",
                autoClose: 4000,
            });
            setErrors(prev => ({ ...(prev || {}), lineItems: "Dispatch Qty missing" }));
            setTimeout(() => {
                const el =
                    document.getElementById("skuTable") ||
                    document.querySelector(".sku-search-results");
                el?.scrollIntoView({ behavior: "smooth", block: "center" });
            }, 120);
            return;
        }

        // -------- helpers (same as your originals) --------
        const pick = (src, fieldsArr) => {
            const set = new Set((fieldsArr || []).map((f) => f.name));
            const out = {};
            Object.keys(src || {}).forEach((k) => {
                if (set.has(k)) out[k] = src[k];
            });
            return out;
        };

        const normalizeUploadObject = (u) => {
            if (!u) return null;
            if (Array.isArray(u)) return u;
            if (typeof u === "object") {
                return Object.keys(u)
                    .sort((a, b) => Number(a) - Number(b))
                    .map((k) => u[k]);
            }
            return null;
        };

        // -------- section picks (same as before) --------
        const header = pick(formData, shipmentHeaderFields);
        const sourcePicked = pick(formData, sourceSellerAddressFields);
        const destPicked = pick(formData, destinationBuyerAddressFields);
        const carrierPicked = pick(formData, carrierFieldsPP);
        const loadPicked = pick(formData, loadFieldsPP);
        const udfPicked = pick(formData, udfFields);

        const headerObj = {
            shipmentNumber: header.shipmentNumber ?? null,
            externalShipmentNumber: header.externalShipmentNumber ?? null,
            loadId: header.loadId ?? null,
            source: header.source ?? null,
            createUsing: header.createUsing ?? null,
            skuSearch: header.skuSearch ?? null,
            orderNumbers: header.orderNumbers ?? null,
            status: header.status ?? "New",
            totalQty: header.totalQty ?? null,
            totalWeight: header.totalWeight ?? null,
            totalVolume: header.totalVolume ?? null,
            shipmentDate: header.shipmentDate ?? null,
            closedDate: header.closedDate ?? null,
            ...header,
        };

        const sourceAddress = {
            sourceName: sourcePicked.sourceName ?? formData.sourceName ?? null,
            addressLine1: sourcePicked.sourceAddressLine1 ?? formData.sourceAddressLine1 ?? null,
            addressLine2: sourcePicked.sourceAddressLine2 ?? formData.sourceAddressLine2 ?? null,
            addressLine3: sourcePicked.sourceAddressLine3 ?? formData.sourceAddressLine3 ?? null,
            addressLine4: sourcePicked.sourceAddressLine4 ?? formData.sourceAddressLine4 ?? null,
            city: sourcePicked.sourceCity ?? formData.sourceCity ?? null,
            state: sourcePicked.sourceState ?? formData.sourceState ?? null,
            country: sourcePicked.sourceCountry ?? formData.sourceCountry ?? null,
            contact: sourcePicked.sourceContact ?? formData.sourceContact ?? null,
            email: sourcePicked.sourceEmail ?? formData.sourceEmail ?? null,
        };

        const destinationAddress = {
            name: destPicked.destinationName ?? formData.destinationName ?? null,
            addressLine1: destPicked.destinationAddressLine1 ?? formData.destinationAddressLine1 ?? null,
            addressLine2: destPicked.destinationAddressLine2 ?? formData.destinationAddressLine2 ?? null,
            addressLine3: destPicked.destinationAddressLine3 ?? formData.destinationAddressLine3 ?? null,
            addressLine4: destPicked.destinationAddressLine4 ?? formData.destinationAddress4 ?? null,
            city: destPicked.destinationCity ?? formData.destinationCity ?? null,
            state: destPicked.destinationState ?? formData.destinationState ?? null,
            country: destPicked.destinationCountry ?? formData.destinationCountry ?? null,
            contact: destPicked.destinationContact ?? formData.destinationContact ?? null,
            email: destPicked.destinationEmail ?? formData.destinationEmail ?? null,
        };

        const billTo = {
            name: formData.billToName ?? null,
            addressLine1: formData.billToAddressLine1 ?? null,
            addressLine2: formData.billToAddressLine2 ?? null,
            addressLine3: formData.billToAddressLine3 ?? null,
            addressLine4: formData.billToAddressLine4 ?? null,
            city: formData.billToCity ?? null,
            state: formData.billToState ?? null,
            country: formData.billToCountry ?? null,
            contact: formData.billToContact ?? null,
            email: formData.billToEmail ?? null,
            billToSameAsDestination: formData.billToSameAsDestination ?? false,
        };

        const invoice = {
            invoiceNumber: formData.invoiceNumber ?? null,
            invoiceDate: formData.invoiceDate ?? null,
            invoiceType: formData.invoiceType ?? null,
            invoiceAmount: formData.invoiceAmount ?? null,
            paymentTerms: formData.paymentTerms ?? null,
            invoiceUpload: normalizeUploadObject(formData.invoiceUpload),
        };

        const carrier = {
            shippingMode: carrierPicked.shippingMode ?? formData.shippingMode ?? null,
            carrierName: carrierPicked.carrierName ?? formData.carrierName ?? null,
            awbNumber: carrierPicked.awbNumber ?? formData.awbNumber ?? null,
            addressLine1: carrierPicked.carrierAddressLine1 ?? formData.carrierAddressLine1 ?? null,
            addressLine2: carrierPicked.carrierAddressLine2 ?? formData.carrierAddressLine2 ?? null,
            city: carrierPicked.carrierCity ?? formData.carrierCity ?? null,
            state: carrierPicked.carrierState ?? formData.carrierState ?? null,
            country: carrierPicked.carrierCountry ?? formData.carrierCountry ?? null,
            contact: carrierPicked.carrierContact ?? formData.carrierContact ?? null,
            email: carrierPicked.carrierEmail ?? formData.carrierEmail ?? null,
        };

        const loadDates = {
            vehicleNumber: loadPicked.vehicleNumber ?? formData.vehicleNumber ?? null,
            vehicleDetails: loadPicked.vehicleDetails ?? formData.vehicleDetails ?? null,
            driverDetails: loadPicked.driverDetails ?? formData.driverDetails ?? null,
            expectedDepartureDate: loadPicked.expectedDepartureDate ?? formData.expectedDepartureDate ?? null,
            actualDepartureDate: loadPicked.actualDepartureDate ?? formData.actualDepartureDate ?? null,
            expectedArrivalDate: loadPicked.expectedArrivalDate ?? formData.expectedArrivalDate ?? null,
            actualArrivalDate: loadPicked.actualArrivalDate ?? formData.actualArrivalDate ?? null,
        };

        const pod = {
            podDetails: formData.podDetails ?? null,
            podUpload: normalizeUploadObject(formData.podUpload),
        };

        const udf = { ...udfPicked };

        // 4) Build stopList from stops and their selected line items
        const stopList = [];

        // Reuse stops variable defined earlier in validation (line 614)

        // Build stopList array - include ALL stops, even if they have no selected items
        stops.forEach((stop) => {
            const stopGroupKey = `STOP_${stop.id}`;

            // Get ALL rows from loadTable for this stop (include all items added to the stop)
            const allStopRows = Array.isArray(loadTable?.[stopGroupKey]) ? loadTable[stopGroupKey] : [];

            const stopLineItems = [];

            // Process ALL rows for this stop (not just selected ones)
            allStopRows.forEach((r, idx) => {
                const rowIndex =
                    Number(r.rowIndex ?? r._rowIndex ?? r.__rowIndex__) ??
                    allStopRows.findIndex(x => x === r);

                const expectedQty = Number(r.expectedQty ?? r.orderedQty ?? 0) || 0;
                const dispatchQty = Number(r.dispatchQty ?? r._derived?.dispatchQty ?? 0) || 0;

                stopLineItems.push({
                    skuId: r.skuId || r.item || "",
                    purchaseOrder: r.purchaseOrder || null,
                    salesOrder: r.salesOrder || null,
                    orderedQty: expectedQty,
                    dispatchQty,
                    description: r.description || "",
                    pack: r.pack || "STD",
                    uom: r.uom || "EA",
                    openQty: Number(r.openQty ?? 0) || 0,
                    lineNumber: r.lineNo || String((rowIndex >= 0 ? rowIndex : idx) + 1).padStart(4, "0"),
                    rowIndex: (rowIndex >= 0 ? rowIndex : idx),
                    qcRequired: r.qcRequired ?? null,
                    receivedQty: r.receivedQty ?? null,
                    receivedDate: r.receivedDate ?? null,
                    grNumber: r.grNumber ?? null,
                    itemWeight: r.itemWeight ?? null,
                    itemVolume: r.itemVolume ?? null,
                    lpn: r.lpn ?? null,
                    lotBatch: r.lotBatch ?? null,
                    manufactureDate: r.manufactureDate ?? null,
                    expiryDate: r.expiryDate ?? null,
                    deliverByDate: r.deliverByDate ?? null,
                    bestByDate: r.bestByDate ?? null,
                    itemUDF: r.itemUDF ?? null,
                });
            });

            // Always include the stop, even if it has no line items
            stopList.push({
                stopListNo: stop.id,
                lineItems: stopLineItems,
            });
        });

        // Also handle non-stop groups (PO::, SKU::, etc.) - add them to the first stop or create a default stop
        const nonStopEntries = selectedEntries.filter(([groupKey]) => !String(groupKey).startsWith("STOP_"));

        if (nonStopEntries.length > 0) {
            // If no stops exist, create a default stop
            if (stopList.length === 0) {
                stopList.push({
                    stopListNo: 1,
                    lineItems: [],
                });
            }

            // Add non-stop items to the first stop
            const firstStop = stopList[0];
            nonStopEntries.forEach(([groupKey, rows]) => {
                const originalGroupRows = Array.isArray(loadTable?.[groupKey]) ? loadTable[groupKey] : [];
                rows.forEach((r, idx) => {
                    const rowIndex =
                        Number(r.rowIndex ?? r._rowIndex ?? r.__rowIndex__) ??
                        originalGroupRows.findIndex(x => x === r);

                    const expectedQty = Number(r.expectedQty ?? r.orderedQty ?? 0) || 0;
                    const dispatchQty = Number(r.dispatchQty ?? r._derived?.dispatchQty ?? 0) || 0;

                    firstStop.lineItems.push({
                        skuId: r.skuId || r.item || "",
                        purchaseOrder: r.purchaseOrder || null,
                        salesOrder: r.salesOrder || null,
                        orderedQty: expectedQty,
                        dispatchQty,
                        description: r.description || "",
                        pack: r.pack || "STD",
                        uom: r.uom || "EA",
                        openQty: Number(r.openQty ?? 0) || 0,
                        lineNumber: r.lineNo || String((rowIndex >= 0 ? rowIndex : idx) + 1).padStart(4, "0"),
                        rowIndex: (rowIndex >= 0 ? rowIndex : idx),
                        qcRequired: r.qcRequired ?? null,
                        receivedQty: r.receivedQty ?? null,
                        receivedDate: r.receivedDate ?? null,
                        grNumber: r.grNumber ?? null,
                        itemWeight: r.itemWeight ?? null,
                        itemVolume: r.itemVolume ?? null,
                        lpn: r.lpn ?? null,
                        lotBatch: r.lotBatch ?? null,
                        manufactureDate: r.manufactureDate ?? null,
                        expiryDate: r.expiryDate ?? null,
                        deliverByDate: r.deliverByDate ?? null,
                        bestByDate: r.bestByDate ?? null,
                        itemUDF: r.itemUDF ?? null,
                    });
                });
            });
        }

        if (stopList.length === 0 || stopList.every(stop => stop.lineItems.length === 0)) {
            setErrors(prev => ({ ...(prev || {}), lineItems: "No items to submit" }));
            toast.error("No items to submit. Please select at least one line.", {
                position: "top-center",
                autoClose: 4000,
            });
            return;
        }

        // 5) Final payload
        const dataForApi = {
            shipmentDetails: headerObj,
            sourceDetail: sourceAddress,
            destination: destinationAddress,
            billTo: billTo,
            invoice: invoice,
            carrier: carrier,
            load: loadDates,
            pod: pod,
            udf: udf,
            stopList: stopList,
            receiptStatus: formData.receiptStatus ?? null,
            shipmentRef: formData.shipmentRef ?? null,
        };

        // 6) API call (same as your flow)
        let loadingToastId = null;
        try {
            // console.log("Outgoing shipment payload (backend shape):", dataForApi);
            setLoading(true);

            const determineEditId = () => {
                const originalRootId =
                    (initialValues && (initialValues._id || initialValues.id)) ?? null;
                const fromFormTop = (formData && (formData._id || formData.id)) ?? null;
                const fallbackShipmentNumber = headerObj.shipmentNumber ?? null;
                return originalRootId ?? fromFormTop ?? fallbackShipmentNumber ?? null;
            };

            const editId = determineEditId();

            // const toastMessage = editId
            //     ? `Updating shipment ${editId}...`
            //     : headerObj.shipmentNumber
            //         ? `Creating shipment ${headerObj.shipmentNumber}...`
            //         : `Processing shipment...`;
            // loadingToastId = toast.loading(toastMessage);

            let result = null;
            if (isEdit) {
                if (!editId) {
                    throw new Error(
                        "Cannot determine shipment identifier for update. Ensure top-level _id or shipmentNumber is present."
                    );
                }
                if (!dataForApi.shipmentDetails) dataForApi.shipmentDetails = {};
                if (!dataForApi.shipmentDetails.shipmentNumber && headerObj.shipmentNumber) {
                    dataForApi.shipmentDetails.shipmentNumber = headerObj.shipmentNumber;
                }

                result = await updateShipment(editId, dataForApi);

                if (loadingToastId) {
                    // toast.dismiss(loadingToastId);
                    loadingToastId = null;
                }
                toast.success(`Shipment updated successfully`, {
                    position: "top-center",
                    autoClose: 3000,
                });
            } else {
                const data = sanitizeFormData(dataForApi);
                result = await createShipment(data);

                const createdId = result?.id ?? result?._id ?? result?.shipmentNumber ?? null;
                if (loadingToastId) {
                    toast.dismiss(loadingToastId);
                    loadingToastId = null;
                }
                toast.success(
                    createdId ? `Shipment ${createdId} created successfully` : "Shipment created successfully",
                    { position: "top-center", autoClose: 3000 }
                );
            }

            onSubmit?.(result);
        } catch (err) {
            console.error("Prepare shipment payload error", err);
            if (loadingToastId) {
                toast.dismiss(loadingToastId);
                loadingToastId = null;
            }
            const message =
                err?.message || err?.response?.data?.message || "Failed to prepare shipment payload";
            toast.error(message, { position: "top-center", autoClose: 4000 });
        } finally {
            if (loadingToastId) {
                toast.dismiss(loadingToastId);
                loadingToastId = null;
            }
            setLoading(false);
        }
    };


    const ActiveComponent = tabs[activeTab].component;

    return (
        <>
            {loading && <Loader />}

            {/* Tabs header with red badge + styling on error (like your reference) */}
            <ul className="nav nav-tabs mb-3">
                {tabs.map((t, i) => {
                    const isActive = activeTab === i;
                    const hasErr = tabHasErrors(i);
                    return (
                        <li key={i} className="nav-item">
                            <button
                                type="button"
                                onClick={() => setActiveTab(i)}
                                className={`nav-link ${isActive ? "active" : ""} ${hasErr ? "text-danger fw-semibold" : ""
                                    }`}
                                style={{
                                    position: "relative",
                                    ...(hasErr
                                        ? {
                                            borderColor: "#dc3545",
                                            color: "#dc3545",
                                            backgroundColor: isActive ? "#f8d7da" : "transparent",
                                        }
                                        : {}),
                                }}
                            >
                                {t.label}
                                {hasErr && (
                                    <span
                                        className="badge bg-danger ms-2"
                                        style={{ fontSize: "0.7em" }}
                                        title="Please fix errors in this section"
                                    >
                                        !
                                    </span>
                                )}
                            </button>
                        </li>
                    );
                })}
            </ul>

            <div className="mb-3">
                <ActiveComponent
                    formData={formData}
                    onChange={handleChange}
                    errors={errors}
                    viewOnly={viewOnly}
                    isEdit={isEdit}
                />
            </div>

            {/* === Stops Section with Tables (Order Number Search) === */}
            <div className="mb-3">
                <StopsSection
                    formData={formData}
                    onChange={handleChange}
                    viewOnly={viewOnly}
                    isEdit={isEdit}
                    renderTablesBelow={true}
                    loadTable={loadTable}
                    onDispatchQtyChange={handleDispatchQtyChange}
                    onRemoveSku={(g) => {
                        setGroupToRemove(g);
                        setOpen(true);
                    }}
                    onSelectionChange={handleSelectionChange}
                    onConfirmDelivery={performConfirmDelivery}
                    onRemoveRow={handleRemoveRow}
                    showConfirmButton={showConfirmButton}
                    createUsing={formData?.createUsing || loadTable.__createUsing}
                />
            </div>

            <hr />

            <div className="d-flex justify-content-center gap-2 p-3">
                <button className="btn btn-secondary" onClick={onCancel}>
                    {viewOnly ? "Back" : "Back"}
                </button>
                {!viewOnly && (
                    <button className="btn btn-primary" onClick={handleSubmit}>
                        {isEdit ? "Update Shipment Order" : "Create Shipment Order"}
                    </button>
                )}
            </div>
        </>
    );
}
